import { AxiosInstance } from "axios"
import { MeetingServiceOrder } from "../../../merchant/accounting/accounting/accountingTypes"
import { AvailabilityDetails, ResourceAvailability } from "./weekMenuTypes"
import { ServiceResponse } from "../../../../core/api/apiTypes"

type OrderActionDetails = {
    allow: boolean
    message?: {
        title: string
        body: string
    }
}

export enum MeetingServiceQuery {
    PUBLIC_ORDER_DETAILS = "PUBLIC_ORDER_DETAILS",
    ORDER_DETAILS = "ORDER_DETAILS",
}

export type MeetingOrderAction = Readonly<{
    updateBooking: OrderActionDetails
    extendBooking: OrderActionDetails
    endBooking: OrderActionDetails
    deleteBooking: OrderActionDetails
    deleteMeetingService: OrderActionDetails
    updateMeetingService: OrderActionDetails
    creditMeetingService?: OrderActionDetails
    duplicateMeetingService?: OrderActionDetails
}>

export type MeetingDetailsOrder = MeetingServiceOrder & Readonly<{ orderAction: MeetingOrderAction }>

export type OrderDetailsResponse = Readonly<{
    orders: Array<MeetingDetailsOrder>
}>

export async function fetchOrderDetails(
    {
        orderId,
        flow,
    }: {
        orderId: number
        flow?: string
    },
    axios: AxiosInstance,
    signal?: AbortSignal,
): Promise<OrderDetailsResponse> {
    const { data } = await axios.get(`/meetingService/orders/${orderId}`, {
        params: {
            flow,
        },
        signal,
    })

    return data
}

type UpdateOrderMeetingDisplayParams = {
    orderId: number
    order: {
        meetingDisplay: {
            meetingName: string
            isCanceled: boolean
        }
    }
}

export async function updateOrderMeetingDisplay(
    { orderId, ...rest }: UpdateOrderMeetingDisplayParams,
    axios: AxiosInstance,
    signal?: AbortSignal,
): Promise<OrderDetailsResponse> {
    const response = await axios.post(`/orders/${orderId}/meetingDisplay`, { ...rest }, { signal })

    return response.data
}

type FetchPublicOrderDetailsParams = {
    orderUid: string
}

export async function fetchPublicOrderDetails(
    { orderUid }: FetchPublicOrderDetailsParams,
    axios: AxiosInstance,
    signal?: AbortSignal,
): Promise<OrderDetailsResponse> {
    const response = await axios.get(`/public/meetingService/orders/${orderUid}`, {
        signal,
    })

    return response.data
}

type FetchOrderFreeBusyScheduleParams = {
    orderId: number
    availabilityViewInterval?: number
    startTime: string
    endTime: string
}

type AvailabilityResponse = {
    availabilityDetails: AvailabilityDetails
    resources: ResourceAvailability[]
}

export async function fetchOrderFreeBusySchedule(
    { orderId, availabilityViewInterval = 5, startTime, endTime }: FetchOrderFreeBusyScheduleParams,
    axios: AxiosInstance,
    signal?: AbortSignal,
): Promise<AvailabilityResponse> {
    const response = await axios.get(`orders/${orderId}/booking/freeBusy`, {
        params: {
            availabilityViewInterval,
            startTime,
            endTime,
        },
        signal,
    })

    return response.data
}

export async function refundOrder(
    { orderId, orderTypes }: { orderId: number; orderTypes: string[] },
    axios: AxiosInstance,
    signal?: AbortSignal,
): Promise<ServiceResponse<{}>> {
    const { data } = await axios.delete(`/orders/${orderId}`, {
        params: {
            delete: orderTypes.join(","),
        },
        signal,
    })
    return data
}

export type EndBookingParams = {
    orderId: number
    endDateTime: string
}

export async function ordersEndBooking(
    { orderId, endDateTime }: EndBookingParams,
    axios: AxiosInstance,
    signal?: AbortSignal,
): Promise<ServiceResponse<{}>> {
    const response = await axios.post(
        `/orders/${orderId}/booking/end`,
        {
            endDateTime: endDateTime,
        },
        {
            signal,
        },
    )

    return response.data
}

export async function ordersExtendBooking(
    { orderId, endDateTime }: EndBookingParams,
    axios: AxiosInstance,
    signal?: AbortSignal,
): Promise<ServiceResponse<{}>> {
    const response = await axios.post(
        `/orders/${orderId}/booking/extend`,
        {
            endDateTime: endDateTime,
        },
        {
            signal,
        },
    )

    return response.data
}
